import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { pagesData, galleriesData } from "../data";
import Lightbox from "../components/Lightbox";
import {
  Image as ImageIcon,
  Car,
  BookOpen,
  FileText,
  Clock,
  HeartHandshake,
  Check,
  Plus,
  AlertTriangle,
  Gift,
  CreditCard,
  Building,
  ChevronRight,
  HelpCircle,
  Percent
} from "lucide-react";

// Mapping of subpage slugs to the background images provided by the user
const subpageImages = {
  "priser": "https://assets.auxodev.dk/assets/354bdcac-d302-42dd-b3d6-dafad7605e7e/86b7dd11-d045-4b03-b2ca-81aee36dc24a.jpg",
  "materialer": "https://assets.auxodev.dk/assets/354bdcac-d302-42dd-b3d6-dafad7605e7e/86b7dd11-d045-4b03-b2ca-81aee36dc24a.jpg",
  "holdstart": "https://assets.auxodev.dk/assets/354bdcac-d302-42dd-b3d6-dafad7605e7e/3410097f-6c08-4059-aee2-0f17fdd0b580.jpg",
  "nyheder": "https://assets.auxodev.dk/assets/354bdcac-d302-42dd-b3d6-dafad7605e7e/96ebd1b3-ee73-46b7-8925-56feee733eab.jpg",
  "foerstehjaelp": "https://assets.auxodev.dk/assets/354bdcac-d302-42dd-b3d6-dafad7605e7e/3410097f-6c08-4059-aee2-0f17fdd0b580.jpg",
  "om-os": "https://assets.auxodev.dk/assets/354bdcac-d302-42dd-b3d6-dafad7605e7e/a93cd808-ec0f-4c53-8d40-68823899cffd.jpg",
  "kontakt": "https://assets.auxodev.dk/assets/354bdcac-d302-42dd-b3d6-dafad7605e7e/a93cd808-ec0f-4c53-8d40-68823899cffd.jpg",
  "vores-ydelser": "https://assets.auxodev.dk/assets/354bdcac-d302-42dd-b3d6-dafad7605e7e/a93cd808-ec0f-4c53-8d40-68823899cffd.jpg"
};

const iconMap = {
  Car: Car,
  BookOpen: BookOpen,
  FileText: FileText,
  Clock: Clock,
  HeartHandshake: HeartHandshake
};

// Structured data for the interactive prices tabs
const priserTabs = [
  {
    id: "kat-b",
    label: "Kørekort til kat. B (almindelig personbil)",
    shortLabel: "Kategori B",
    icon: "Car",
    badge: "Mest populær",
    title: "Kørekort til kat. B (almindelig personbil)",
    price: "16.900 kr.",
    priceLabel: "Lovpakken i alt",
    description: "Alt hvad du skal bruge for at erhverve dit kørekort til almindelig personbil på en tryg og effektiv måde.",
    lovpakkeItems: [
      { name: "Teorikursus med kørelærer (ubegrænset i 6 måneder)", price: "2.500 kr." },
      { name: "4 x lektioner på lukket øvelsesplads (manøvrebane) inkl. transport", price: "2.300 kr." },
      { name: "4 x lektioner på køreteknisk anlæg (glatbane) inkl. transport", price: "2.600 kr." },
      { name: "16 x kørelektioner på vej", price: "10.400 kr." },
      { name: "PAKKERABAT", price: "-900 kr.", isDiscount: true },
    ],
    extraPrices: [
      { name: "Prøvegebyr til Færdselsstyrelsen (betales via www.koreprovebooking.dk)", price: "1.600 kr." },
      { name: "Færdselsrelateret førstehjælpskursus", price: "650 kr." },
      { name: "Leje af skolevogn til køreprøve", price: "800 kr." },
      { name: "Evt. ekstra kørelektioner", price: "650 kr." },
      { name: "Lægeerklæring (betales til egen læge) ca.", price: "500 kr." },
      { name: "Pasfoto (afleveres til køreskolen) ca.", price: "100 kr." },
    ],
    infoBox: [
      { type: "gift", text: "Betaler du lovpakke + leje af skolevogn til køreprøve indenfor 8 dage efter holdstart får du 1 x gratis kørelektion (værdi 700 kr.)" },
      { type: "card", text: "Lovpakke + leje af skolevogn til køreprøve kan betales over 3 rater: første rate på 6.000 kr. ved holdstart & sidste rate INDEN teoriprøven." },
      { type: "alert", text: "Med mindre andet aftales, skal kørelektioner afmeldes senest dagen inden aftalt tid og inden kl. 18:00, ellers afregnes gebyr på 700 kr." },
      { type: "bank", text: "Betaling foregår ved bankoverførsel til Jyske Bank 7170-2765794 eller ved MobilePay til 78397." },
      { type: "clock", text: "1 x lektion = 45 minutter." }
    ]
  },
  {
    id: "saerlig",
    label: "Særlig køreundervisning",
    shortLabel: "Særlig undervisning",
    icon: "BookOpen",
    title: "Særlig køreundervisning",
    price: "7.500 kr.",
    priceLabel: "Lovpakken i alt",
    description: "Særlig køreundervisning til dig, der har fået kørselsforbud eller skal generhverve kørekortet under særlige vilkår.",
    lovpakkeItems: [
      { name: "8 x teorilektioner", price: "1.500 kr." },
      { name: "8 x kørelektioner", price: "5.200 kr." },
      { name: "1 x Leje af skolevogn til køreprøve", price: "800 kr." },
    ],
    extraPrices: [
      { name: "Prøvegebyr til Færdselsstyrelsen (betales via www.koreprovebooking.dk)", price: "1.690 kr." },
      { name: "Evt. ekstra kørelektioner", price: "650 kr." },
      { name: "Pasfoto ca.", price: "100 kr." },
    ],
    infoBox: [
      { type: "bank", text: "Betaling foregår ved bankoverførsel til Jyske Bank 7170-2765794 eller ved MobilePay til 78397." },
      { type: "clock", text: "1 x lektion = 45 minutter." }
    ]
  },
  {
    id: "kontrollerende",
    label: "Kontrollerende køreprøve",
    shortLabel: "Kontrollerende prøve",
    icon: "FileText",
    title: "Kontrollerende køreprøve",
    price: "4.200 kr.",
    priceLabel: "Pakken i alt",
    description: "Kontrollerende Køreprøve består både af en teoretisk prøve (teoriprøve) og en praktisk prøve (køreprøve).",
    lovpakkeItems: [
      { name: "Frit teoriforløb i 3 måneder", price: "1.500 kr." },
      { name: "2 x kørelektioner", price: "1.300 kr." },
      { name: "1 x leje af skolevogn til køreprøve", price: "800 kr." },
      { name: "Administration", price: "600 kr." },
    ],
    extraPrices: [
      { name: "Prøvegebyr til Færdselsstyrelsen (betales via www.koreprovebooking.dk)", price: "1.690 kr." },
      { name: "Evt. ekstra kørelektioner", price: "650 kr." },
      { name: "Evt. pasfoto ca.", price: "100 kr." },
    ],
    infoBox: [
      { type: "bank", text: "Betaling foregår ved bankoverførsel til Jyske Bank 7170-2765794 eller ved MobilePay til 78397." },
      { type: "clock", text: "1 x lektion = 45 minutter." }
    ]
  },
  {
    id: "rutine",
    label: "Rutine timer",
    shortLabel: "Rutinetimer",
    icon: "Clock",
    title: "Rutine timer",
    price: "800 kr.",
    priceLabel: "Pris pr. time (60 min)",
    description: "Er det længe siden du sidst har kørt bil? Har du fået en dårlig oplevelse i trafikken? Føler du dig ikke tryg bag rattet længere? Så kan Køreskolen AG hjælpe dig med at få modet tilbage igen.",
    lovpakkeItems: [
      { name: "Pris pr. rutinetime (60 minutter)", price: "800 kr." }
    ],
    extraPrices: [],
    infoBox: [
      { type: "bank", text: "Betaling foregår ved bankoverførsel til Jyske Bank 7170-2765794 eller ved MobilePay til 78397." }
    ]
  },
  {
    id: "foerstehjaelp",
    label: "Færdselsrelateret førstehjælpskursus",
    shortLabel: "Førstehjælp",
    icon: "HeartHandshake",
    title: "Færdselsrelateret førstehjælpskursus",
    price: "650 kr.",
    priceLabel: "Kursuspris",
    description: "Du kan tage det lovpligtige færdselsrelaterede førstehjælpskursus hos Køreskolen AG. Førstehjælpskursus afvikles i weekenderne (1 dag). Elever udenfor Køreskolen AG er også velkomne.",
    lovpakkeItems: [
      { name: "Færdselsrelateret førstehjælpskursus (1 dag)", price: "650 kr." }
    ],
    extraPrices: [],
    infoBox: [
      { type: "alert", text: "Tilmeld dig på førstehjælpskursus med SMS på tlf. nr. 60697999" },
      { type: "card", text: "Betaling afregnes på selve kursusdagen." }
    ]
  }
];

// Helper to get clean page title without suffix
const getCleanTitle = (title) => {
  if (!title) return "";
  const parts = String(title).split(/ - | \| /);
  return parts[0].trim();
};

// Helper to safely extract localized string from CMS field
const getLocalizedText = (val, fallback = "") => {
  if (!val) return fallback;
  if (typeof val === "string") return val;
  if (typeof val === "object") {
    if (val.da !== undefined && val.da !== null) return String(val.da);
    const firstVal = Object.values(val)[0];
    if (firstVal !== undefined && firstVal !== null) return String(firstVal);
  }
  return fallback;
};

// Helper to strip the first <h1> and any immediately following <hr> from the content
const stripFirstH1AndHr = (html) => {
  if (!html) return "";
  // Remove the first <h1>...</h1> tag
  let cleaned = html.replace(/<h1[^>]*>[\s\S]*?<\/h1>/i, "");
  // Remove the horizontal rule if it's right after
  cleaned = cleaned.replace(/^\s*<hr[^>]*>/i, "");
  return cleaned.trim();
};

// Helper to parse structured price HTML from CMS into clean cards matching original layout
const parsePriceHtml = (html) => {
  if (!html || typeof window === "undefined") {
    return { lovpakkeItems: [], extraPrices: [], additionalNotes: [] };
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    const lovpakkeItems = [];
    const extraPrices = [];
    const additionalNotes = [];

    let currentSection = "lovpakke";

    const parseListItem = (text) => {
      const clean = text.replace(/\s+/g, " ").trim();
      if (!clean) return null;

      const equalIdx = clean.indexOf("=");
      if (equalIdx !== -1) {
        const name = clean.slice(0, equalIdx).trim();
        const price = clean.slice(equalIdx + 1).trim();
        const isDiscount = name.toLowerCase().includes("rabat") || price.startsWith("-");
        return { name, price, isDiscount };
      }

      return { name: clean, price: "", isDiscount: false };
    };

    const children = Array.from(doc.body.children);

    for (const node of children) {
      const text = node.textContent?.trim() || "";
      const lower = text.toLowerCase();

      if (lower.startsWith("tillægspriser") || lower.startsWith("ekstra ydelser") || lower.includes("tillægspriser:")) {
        currentSection = "extra";
        continue;
      }
      if (lower.startsWith("lovpakken") || lower.startsWith("pakken") || lower.startsWith("ydelse")) {
        currentSection = "lovpakke";
        continue;
      }

      if (node.tagName === "UL" || node.tagName === "OL") {
        const lis = Array.from(node.querySelectorAll("li"));
        for (const li of lis) {
          const item = parseListItem(li.textContent || "");
          if (item) {
            if (currentSection === "extra") {
              extraPrices.push(item);
            } else {
              lovpakkeItems.push(item);
            }
          }
        }
      } else if (node.tagName === "P") {
        const isHeader = (node.querySelector("strong, b") && (lower.includes("lovpakken") || lower.includes("tillægspriser") || lower.includes("pakken indeholder")));
        if (isHeader) {
          if (lower.includes("tillægspriser")) {
            currentSection = "extra";
          } else {
            currentSection = "lovpakke";
          }
        } else if (text) {
          if (text.includes(" = ") || (text.includes("=") && !text.includes("<"))) {
            const item = parseListItem(text);
            if (item) {
              if (currentSection === "extra") {
                extraPrices.push(item);
              } else {
                lovpakkeItems.push(item);
              }
            }
          } else {
            additionalNotes.push(text);
          }
        }
      }
    }

    return { lovpakkeItems, extraPrices, additionalNotes };
  } catch (err) {
    console.error("Fejl ved parsing af pris HTML:", err);
    return { lovpakkeItems: [], extraPrices: [], additionalNotes: [] };
  }
};

export default function PageRenderer({ customSlug, onlyHero = false }) {
  const location = useLocation();
  const slug = customSlug || location.pathname.substring(1) || "index";

  const [page, setPage] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [activeTab, setActiveTab] = useState("kat-b");
  const [dynamicPrices, setDynamicPrices] = useState(null);

  useEffect(() => {
    const fetchPageData = async () => {
      setLoading(true);
      try {
        // 1. Fetch page from new Danish CMS collection 'sider'
        const siderRes = await fetch("https://api.dev.auxo.dk/public/content/sider");
        if (siderRes.ok) {
          const sider = await siderRes.json();
          const matchedPage = sider.find((p) => {
            const termsList = p.terms?.["side-sti"] || [];
            return (
              termsList.includes(slug) ||
              p.side_sti === slug ||
              p.page_slug === slug
            );
          });

          if (matchedPage) {
            setPage({
              title: getLocalizedText(matchedPage.titel, matchedPage.title || ""),
              meta_description: getLocalizedText(matchedPage.meta_beskrivelse, matchedPage.meta_description || ""),
              content_html: getLocalizedText(matchedPage.indhold_html, matchedPage.content_html || ""),
              images: matchedPage.billeder || matchedPage.images || []
            });
          } else {
            setPage(pagesData[slug]);
          }
        } else {
          setPage(pagesData[slug]);
        }

        // 2. Fetch gallery from new Danish CMS collection 'galleri'
        const galleriRes = await fetch("https://api.dev.auxo.dk/public/content/galleri");
        if (galleriRes.ok) {
          const galleri = await galleriRes.json();
          const matchedGallery = galleri.find((g) => {
            const termsList = g.terms?.["side-sti"] || [];
            const refs = g.side_referencer || [];
            return (
              termsList.includes(slug) ||
              refs.includes(slug) ||
              g.slug_reference === slug
            );
          });

          if (matchedGallery) {
            setGallery({
              title: getLocalizedText(matchedGallery.titel, matchedGallery.title || ""),
              images: matchedGallery.billeder || matchedGallery.images || []
            });
          } else {
            setGallery(galleriesData[slug]);
          }
        } else {
          setGallery(galleriesData[slug]);
        }

        // 3. If on 'priser', fetch dynamic price list from 'priser' collection
        if (slug === "priser") {
          const priserRes = await fetch("https://api.dev.auxo.dk/public/content/priser");
          if (priserRes.ok) {
            const priserData = await priserRes.json();
            if (priserData && priserData.length > 0) {
              const sortedPrices = [...priserData].sort((a, b) => (a.sortering || 99) - (b.sortering || 99));
              setDynamicPrices(sortedPrices);
              setActiveTab((prev) => {
                const exists = sortedPrices.some((p) => p.id === prev);
                return exists ? prev : sortedPrices[0].id;
              });
            }
          }
        }
      } catch (error) {
        console.error("Fejl ved hentning af CMS data:", error);
        setPage(pagesData[slug]);
        setGallery(galleriesData[slug]);
      } finally {
        setLoading(false);
      }
    };

    fetchPageData();
  }, [slug]);

  if (loading && !page) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00D492]"></div>
      </div>
    );
  }

  // Fallback if page still doesn't exist
  const activePage = page || pagesData[slug];
  if (!activePage) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
        <h1 className="text-4xl font-bold text-slate-800 mb-4">404 - Siden blev ikke fundet</h1>
        <p className="text-slate-600 mb-6">Vi kunne desværre ikke finde den side, du leder efter.</p>
        <a href="/" className="px-6 py-3 bg-[#00D492] text-white font-bold rounded-md hover:bg-[#00bf80] transition-colors">
          Gå til Forside
        </a>
      </div>
    );
  }

  const activeGallery = gallery || galleriesData[slug];
  const cleanTitle = getCleanTitle(activePage.title);
  const bgImage = subpageImages[slug] || "https://assets.auxodev.dk/assets/354bdcac-d302-42dd-b3d6-dafad7605e7e/86b7dd11-d045-4b03-b2ca-81aee36dc24a.jpg";

  if (onlyHero) {
    return (
      <div className="bg-white">
        <Helmet>
          <title>{activePage.title} | Køreskolen AG</title>
          {activePage.meta_description && (
            <meta name="description" content={activePage.meta_description} />
          )}
          <meta property="og:title" content={`${activePage.title} | Køreskolen AG`} />
          {activePage.meta_description && (
            <meta property="og:description" content={activePage.meta_description} />
          )}
        </Helmet>

        {/* Premium Subpage Hero Section */}
        <section className="relative isolate overflow-hidden bg-slate-950 text-white h-[400px] sm:h-auto flex items-end sm:block">
          <div className="absolute inset-0">
            <img
              src={bgImage}
              alt={cleanTitle}
              className="h-full w-full object-cover object-center opacity-35"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/35 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-black/10" />
          </div>

          <div className="relative z-10 mx-auto max-w-7xl px-4 pt-32 pb-12 sm:px-6 sm:pt-40 sm:pb-20 lg:px-8 lg:pt-48 lg:pb-24 w-full">
            <div className="max-w-3xl">
              {/* Breadcrumb */}
              <div className="mb-4 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#00D492]">
                <span>Køreskolen AG</span>
                <span className="text-white/30">/</span>
                <span className="text-white/80">{cleanTitle}</span>
              </div>

              <h1 className="text-3xl font-black uppercase tracking-tight text-white sm:text-4xl lg:text-5xl">
                {cleanTitle}
              </h1>
              
              {activePage.meta_description && (
                <p className="mt-4 max-w-2xl text-sm leading-relaxed text-white/75 sm:text-base">
                  {activePage.meta_description}
                </p>
              )}
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Map dynamic prices from CMS if available, otherwise use hardcoded fallback
  const activeTabs = (dynamicPrices && dynamicPrices.length > 0)
    ? dynamicPrices.map((item, idx) => {
        const id = item.id || `tab-${idx}`;
        const label = getLocalizedText(item.titel, item.title || "");
        const shortLabel = getLocalizedText(item.kort_titel, label);
        const price = getLocalizedText(item.pris, item.price || "");
        const priceLabel = getLocalizedText(item.pris_etiket, "Lovpakken i alt");
        const description = getLocalizedText(item.beskrivelse, "");
        const badge = getLocalizedText(item.fremhaevet_baand, "");

        const iconSlug = item.terms?.["ikon-typer"]?.[0] || "";
        let icon = "Car";
        if (iconSlug === "bog") icon = "BookOpen";
        else if (iconSlug === "dokument") icon = "FileText";
        else if (iconSlug === "ur") icon = "Clock";
        else if (iconSlug === "haandtryk") icon = "HeartHandshake";
        else if (iconSlug === "bil") icon = "Car";

        const contentHtml = getLocalizedText(item.indhold_html, item.content_html || "");
        const parsed = parsePriceHtml(contentHtml);

        const fallbackItem = priserTabs[idx] || priserTabs.find((p) => p.title.toLowerCase() === label.toLowerCase()) || {};

        const lovpakkeItems = parsed.lovpakkeItems.length > 0
          ? parsed.lovpakkeItems
          : (fallbackItem.lovpakkeItems || (price ? [{ name: label, price: price }] : []));

        const extraPrices = parsed.extraPrices.length > 0
          ? parsed.extraPrices
          : (fallbackItem.extraPrices || []);

        const infoBox = fallbackItem.infoBox || [
          { type: "bank", text: "Betaling foregår ved bankoverførsel til Jyske Bank 7170-2765794 eller ved MobilePay til 78397." },
          { type: "clock", text: "1 x lektion = 45 minutter." }
        ];

        return {
          id,
          label,
          shortLabel,
          icon,
          badge,
          title: label,
          price,
          priceLabel,
          description,
          contentHtml,
          lovpakkeItems,
          extraPrices,
          additionalNotes: parsed.additionalNotes,
          infoBox
        };
      })
    : priserTabs;

  // Find active tab data
  const currentTabData = activeTabs.find((t) => t.id === activeTab) || activeTabs[0] || priserTabs[0];

  const renderInfoIcon = (type) => {
    switch (type) {
      case "gift":
        return <Gift className="w-5 h-5 text-[#00D492] shrink-0" />;
      case "card":
        return <CreditCard className="w-5 h-5 text-[#00D492] shrink-0" />;
      case "alert":
        return <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />;
      case "bank":
        return <Building className="w-5 h-5 text-[#00D492] shrink-0" />;
      case "clock":
        return <Clock className="w-5 h-5 text-[#00D492] shrink-0" />;
      default:
        return <HelpCircle className="w-5 h-5 text-[#00D492] shrink-0" />;
    }
  };

  return (
    <div className="bg-white min-h-[40vh]">
      <Helmet>
        <title>{activePage.title} | Køreskolen AG</title>
        {activePage.meta_description && (
          <meta name="description" content={activePage.meta_description} />
        )}
        <meta property="og:title" content={`${activePage.title} | Køreskolen AG`} />
        {activePage.meta_description && (
          <meta property="og:description" content={activePage.meta_description} />
        )}
      </Helmet>

      {/* Scoped custom rich text styles with zero specificity using :where() */}
      <style>{`
        .custom-rich-text {
          overflow-wrap: anywhere;
          word-break: break-word;
        }
        .custom-rich-text :where(p) {
          color: #475569;
          line-height: 1.75;
          margin-bottom: 1.25rem;
          overflow-wrap: anywhere;
        }
        .custom-rich-text :where(h2) {
          font-size: 1.65rem;
          font-weight: 800;
          color: #0f172a;
          margin-top: 2.5rem;
          margin-bottom: 1.25rem;
          line-height: 1.3;
          border-bottom: 2px solid #f1f5f9;
          padding-bottom: 0.5rem;
          overflow-wrap: anywhere;
        }
        .custom-rich-text :where(h3) {
          font-size: 1.35rem;
          font-weight: 700;
          color: #0f172a;
          margin-top: 2rem;
          margin-bottom: 1rem;
          line-height: 1.4;
          overflow-wrap: anywhere;
        }
        .custom-rich-text :where(h4) {
          font-size: 1.15rem;
          font-weight: 700;
          color: #0f172a;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
          overflow-wrap: anywhere;
        }
        .custom-rich-text :where(a) {
          color: #00D492;
          text-decoration: underline;
          font-weight: 600;
          transition: color 0.2s ease;
          overflow-wrap: anywhere;
        }
        .custom-rich-text :where(a:hover) {
          color: #00bf80;
        }
        .custom-rich-text :where(strong) {
          font-weight: 700;
          color: #0f172a;
        }
        .custom-rich-text :where(hr) {
          border: 0;
          border-top: 1px solid #e2e8f0;
          margin: 2.5rem 0;
        }
        .custom-rich-text :where(blockquote) {
          border-left: 4px solid #00D492;
          padding-left: 1.25rem;
          font-style: italic;
          margin: 1.75rem 0;
          color: #334155;
          overflow-wrap: anywhere;
        }
        .custom-rich-text :where(ul) {
          list-style-type: disc;
          padding-left: 1.5rem;
          margin-bottom: 1.25rem;
        }
        .custom-rich-text :where(ol) {
          list-style-type: decimal;
          padding-left: 1.5rem;
          margin-bottom: 1.25rem;
        }
        .custom-rich-text :where(li) {
          margin-bottom: 0.5rem;
          line-height: 1.75;
          color: #475569;
          overflow-wrap: anywhere;
        }
        .custom-rich-text :where(table) {
          width: 100%;
          border-collapse: collapse;
          text-align: left;
          font-size: 0.9rem;
          margin: 2rem 0;
          table-layout: fixed;
        }
        .custom-rich-text :where(tr) {
          border-bottom: 1px solid #f1f5f9;
        }
        .custom-rich-text :where(td) {
          padding: 0.75rem 1rem 0.75rem 0;
          line-height: 1.5;
          color: #475569;
          overflow-wrap: anywhere;
        }
        .custom-rich-text :where(th) {
          padding: 0.75rem 1rem 0.75rem 0;
          font-weight: 700;
          color: #0f172a;
          overflow-wrap: anywhere;
        }
      `}</style>

      {/* Premium Subpage Hero Section */}
      <section className="relative isolate overflow-hidden bg-slate-950 text-white h-[400px] sm:h-auto flex items-end sm:block">
        <div className="absolute inset-0">
          <img
            src={bgImage}
            alt={cleanTitle}
            className="h-full w-full object-cover object-center opacity-35"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/35 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-black/10" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 pt-32 pb-12 sm:px-6 sm:pt-40 sm:pb-20 lg:px-8 lg:pt-48 lg:pb-24 w-full">
          <div className="max-w-3xl">
            {/* Breadcrumb */}
            <div className="mb-4 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#00D492]">
              <span>Køreskolen AG</span>
              <span className="text-white/30">/</span>
              <span className="text-white/80">{cleanTitle}</span>
            </div>

            <h1 className="text-3xl font-black uppercase tracking-tight text-white sm:text-4xl lg:text-5xl">
              {cleanTitle}
            </h1>
            
            {activePage.meta_description && (
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-white/75 sm:text-base">
                {activePage.meta_description}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Main Content Container */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-20">
        {slug === "priser" ? (
          <div className="space-y-12">
            {/* Introduction */}
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight sm:text-4xl">
                Se vores priser her
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                Vi tilbyder gennemskuelige priser uden skjulte gebyrer. Vælg mellem vores forskellige pakker og ydelser nedenfor for at se detaljer og priser.
              </p>
            </div>

            {/* Interactive Tabs Selector */}
            <div className="border-b border-slate-200 pb-4 md:pb-0">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:flex md:flex-row overflow-visible pt-3 pb-px -mb-px gap-2 md:gap-4 no-scrollbar scroll-smooth snap-x">
                {activeTabs.map((tab) => {
                  const TabIcon = iconMap[tab.icon];
                  const isActive = currentTabData?.id === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`relative flex items-center gap-2 px-3 py-2.5 md:px-4 md:py-3.5 text-xs md:text-sm font-bold transition-all duration-200 rounded-xl md:rounded-t-lg md:rounded-b-none border md:border-0 md:border-b-2 ${
                        isActive
                          ? "border-[#00D492] text-[#00D492] bg-[#00D492]/5"
                          : "border-slate-200 md:border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50"
                      }`}
                    >
                      {TabIcon && <TabIcon className={`w-4 h-4 shrink-0 ${isActive ? "text-[#00D492]" : "text-slate-400"}`} />}
                      <span className="text-left leading-tight pr-2">{tab.shortLabel || tab.label}</span>
                      {tab.badge && (
                        <span className="absolute -top-2.5 right-2 px-2 py-0.5 text-[8px] md:text-[9px] font-black uppercase tracking-wider bg-[#00D492] text-white rounded-full shadow-md shrink-0 z-10">
                          {tab.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Active Tab Content */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
              {/* Left Column: Title, Description & Package Items */}
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-slate-50 p-6 md:p-8 rounded-2xl border border-slate-100">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                    <div>
                      <h3 className="text-xl md:text-2xl font-black text-slate-900 uppercase tracking-tight">
                        {currentTabData.title}
                      </h3>
                      {currentTabData.description && (
                        <p className="text-sm text-slate-500 mt-1">
                          {currentTabData.description}
                        </p>
                      )}
                    </div>
                    {/* Price Badge */}
                    {currentTabData.price && (
                      <div className="bg-white px-5 py-3.5 rounded-xl border border-slate-200 shadow-sm flex flex-col items-end shrink-0">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                          {currentTabData.priceLabel}
                        </span>
                        <span className="text-2xl font-black text-[#00D492]">
                          {currentTabData.price}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Lovpakken / Package Items */}
                  {currentTabData.lovpakkeItems && currentTabData.lovpakkeItems.length > 0 ? (
                    <div className="space-y-4">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 border-b border-slate-200 pb-2">
                        {currentTabData.lovpakkeItems.length === 1 ? "Ydelse" : (currentTabData.priceLabel?.toLowerCase().includes("lovpakke") ? "Lovpakken indeholder" : "Pakken indeholder")}
                      </h4>
                      <div className="divide-y divide-slate-100">
                        {currentTabData.lovpakkeItems.map((item, idx) => (
                          <div key={idx} className="py-3.5 flex items-start justify-between gap-4">
                            <div className="flex items-start gap-3">
                              <div className={`mt-0.5 p-1 rounded-full ${item.isDiscount ? "bg-emerald-50 text-emerald-600" : "bg-[#00D492]/10 text-[#00D492]"}`}>
                                {item.isDiscount ? <Percent className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
                              </div>
                              <span className={`text-sm md:text-base ${item.isDiscount ? "font-bold text-emerald-600" : "text-slate-700"}`}>
                                {item.name}
                              </span>
                            </div>
                            {item.price && (
                              <span className={`text-sm md:text-base font-bold shrink-0 ${item.isDiscount ? "text-emerald-600" : "text-slate-900"}`}>
                                {item.price}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Total Row for Lovpakken if multiple items */}
                      {currentTabData.lovpakkeItems.length > 1 && currentTabData.price && (
                        <div className="mt-4 p-4 bg-white rounded-xl border border-slate-200 flex justify-between items-center">
                          <span className="font-extrabold text-slate-900">Samlet pris for pakken</span>
                          <span className="text-xl font-black text-[#00D492]">{currentTabData.price}</span>
                        </div>
                      )}
                    </div>
                  ) : currentTabData.contentHtml ? (
                    <div 
                      className="mt-4 prose prose-slate max-w-none text-slate-700 leading-relaxed text-sm"
                      dangerouslySetInnerHTML={{ __html: currentTabData.contentHtml }}
                    />
                  ) : null}

                  {/* Additional notes from CMS (e.g. promo or extra info) */}
                  {currentTabData.additionalNotes && currentTabData.additionalNotes.length > 0 && (
                    <div className="mt-6 pt-4 border-t border-slate-200/80 space-y-2">
                      {currentTabData.additionalNotes.map((note, idx) => (
                        <div key={idx} className="flex items-start gap-2.5 bg-white/80 p-3.5 rounded-xl border border-slate-200/80 text-xs md:text-sm text-slate-600 leading-relaxed">
                          <Check className="w-4 h-4 text-[#00D492] shrink-0 mt-0.5" />
                          <span>{note}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Tillægspriser Card */}
                {currentTabData.extraPrices && currentTabData.extraPrices.length > 0 && (
                  <div className="bg-white p-6 md:p-8 rounded-2xl border border-slate-200 shadow-sm">
                    <h4 className="text-sm font-bold uppercase tracking-wider text-slate-900 mb-4 pb-2 border-b border-slate-100 flex items-center gap-2">
                      <Plus className="w-4 h-4 text-[#00D492]" />
                      Tillægspriser & Ekstra ydelser
                    </h4>
                    <div className="divide-y divide-slate-100">
                      {currentTabData.extraPrices.map((extra, idx) => (
                        <div key={idx} className="py-3 flex justify-between items-center gap-4">
                          <span className="text-sm text-slate-600">{extra.name}</span>
                          <span className="text-sm font-bold text-slate-900 shrink-0">{extra.price}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column: Info Box / Terms */}
              <div className="space-y-6">
                <div className="bg-slate-900 text-white p-6 md:p-8 rounded-2xl border border-slate-800 shadow-xl relative overflow-hidden">
                  {/* Decorative background glow */}
                  <div className="absolute -right-10 -top-10 w-40 h-40 bg-[#00D492]/10 rounded-full blur-2xl pointer-events-none" />
                  
                  <h4 className="text-base font-bold uppercase tracking-wider text-white mb-6 pb-3 border-b border-white/10 flex items-center gap-2">
                    <HelpCircle className="w-5 h-5 text-[#00D492]" />
                    Vigtig information
                  </h4>

                  <div className="space-y-5">
                    {currentTabData.infoBox.map((info, idx) => (
                      <div key={idx} className="flex items-start gap-3.5">
                        {renderInfoIcon(info.type)}
                        <p className="text-sm leading-relaxed text-slate-300">
                          {info.text}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* Call to Action */}
                  <div className="mt-8 pt-6 border-t border-white/10">
                    <p className="text-xs text-slate-400 mb-4">
                      Klar til at starte eller har du yderligere spørgsmål?
                    </p>
                    <div className="grid grid-cols-1 gap-3">
                      <a
                        href="/kontakt"
                        className="w-full py-3 px-4 bg-[#00D492] hover:bg-[#00bf80] text-slate-950 font-bold text-sm rounded-xl text-center transition-all duration-200 shadow-lg shadow-[#00D492]/15"
                      >
                        Tilmeld dig nu
                      </a>
                      <a
                        href="tel:60697999"
                        className="w-full py-3 px-4 bg-white/5 hover:bg-white/10 text-white font-bold text-sm rounded-xl text-center transition-all duration-200 border border-white/10"
                      >
                        Ring til os
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className={`${slug === "nyheder" ? "max-w-7xl" : "max-w-4xl"} mx-auto`}>
            {/* Main Content */}
            <article
              className="custom-rich-text max-w-none"
              dangerouslySetInnerHTML={{ __html: stripFirstH1AndHr(activePage.content_html) }}
            />
          </div>
        )}

        {/* Gallery Section */}
        {activeGallery && activeGallery.images && activeGallery.images.length > 0 && (
          <section className="mt-16 pt-12 border-t border-slate-100 max-w-4xl mx-auto">
            <div className="flex items-center gap-2.5 mb-6">
              <ImageIcon className="w-6 h-6 text-[#00D492]" />
              <h3 className="text-2xl font-bold text-slate-900">
                {activeGallery.title || "Billedgalleri"}
              </h3>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {activeGallery.images.map((imgUrl, index) => (
                <div
                  key={index}
                  onClick={() => setLightboxIndex(index)}
                  className="aspect-square overflow-hidden rounded-lg border border-slate-100 shadow-sm hover:shadow-md cursor-pointer group relative bg-slate-50"
                >
                  <img
                    src={imgUrl}
                    alt={`Galleri billede ${index + 1}`}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="text-white text-xs font-bold bg-black/60 px-3 py-1.5 rounded-full">
                      Forstør
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Lightbox */}
      {activeGallery && lightboxIndex !== null && (
        <Lightbox
          images={activeGallery.images}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onPrev={() => setLightboxIndex((prev) => (prev === 0 ? activeGallery.images.length - 1 : prev - 1))}
          onNext={() => setLightboxIndex((prev) => (prev === activeGallery.images.length - 1 ? 0 : prev + 1))}
        />
      )}
    </div>
  );
}
